import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import type { GetDynamicInvoiceDataResponse } from '@vy/api-definition';
import { dynamicInvoicePageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import {
  isDraftInvoiceApprovable,
  isInvoiceApprovable,
  isInvoiceCustomerChildOfAccumulation,
} from '@vy/invoices';
import { formatPrice } from '@vy/services';
import { INVOICE_REVIEW_STATUSES, type Currency } from '@vy/types';
import { isNotEmptyString, isNotNullOrUndefined, withDefault } from '@vy/utils';
import React, { useCallback } from 'react';
import type { QueryKey } from 'react-query';
import { useQueryClient } from 'react-query';
import { NavLink } from 'react-router-dom';
import { EditableTextLabel } from '../../../../../components/new-design/inputs/EditableLabel';
import { useExchangeRatesMatrix } from '../../../../../hooks/requests/exchange-rates/use-exchange-rates-matrix';
import { useAccount } from '../../../../../hooks/requests/use-account';
import { useUpdateInvoiceIssueDate } from '../../../../../hooks/requests/use-update-invoice-issue-date';
import { useUpdateInvoicesReviewStatuses } from '../../../../../hooks/requests/use-update-invoices-review-statuses';
import { useDialog } from '../../../../../hooks/use-dialog';
import { useNotificationTimeout } from '../../../../../hooks/use-notification-timeout';
import { useVyNavigate } from '../../../../../hooks/use-vy-navigate';
import { runMutation } from '../../../../../utils/vy-mutation';
import { DynamicInvoiceApprovedLabel } from './dynamic-invoice-header-components/DynamicInvoiceApprovedLabel';
import { DynamicInvoiceDetail } from './dynamic-invoice-header-components/DynamicInvoiceDetails';
import { InvoiceHeaderInfo } from './dynamic-invoice-header-components/DynamicInvoiceHeaderInfo';
import { DynamicInvoiceIssueDate } from './dynamic-invoice-header-components/DynamicInvoiceIssueDate';
import { ExchangeRateDialog } from './dynamic-invoice-header-components/ExchangeRateDialog';
import { ExchangeRateDisplay } from './dynamic-invoice-header-components/ExchangeRateDisplay';
import { useExchangeRateData } from './dynamic-invoice-header-hooks/use-exchange-rate-data';
import { useExchangeRateDialog } from './dynamic-invoice-header-hooks/use-exchange-rate-dialog';
import { useExchangeRateForm } from './dynamic-invoice-header-hooks/use-exchange-rate-form';
import { useUpdateInvoiceNumber } from './dynamic-invoice-header-hooks/use-update-invoice-number';
import { ApproveDraftInvoiceDialog } from '../../../invoices-components/approve-draft-invoice-dialog/ApproveDraftInvoiceDialog';

type DynamicInvoiceHeaderProps = {
  dynamicInvoiceDataResponse: GetDynamicInvoiceDataResponse;
  currency: Currency;
  contractCurrency: Currency;
  queryKeyToInvalidate?: QueryKey;
  noRedirectOnApprove?: boolean;
};

export const DynamicInvoiceHeader: React.FC<DynamicInvoiceHeaderProps> = ({
  dynamicInvoiceDataResponse: { dynamicInvoiceData },
  currency,
  contractCurrency,
  queryKeyToInvalidate: _queryKeyToInvalidate,
  noRedirectOnApprove = false,
}) => {
  const { invoice, customer } = dynamicInvoiceData;
  const { id: invoiceId, revenueBreakdown, reviewStatus, contractId } = invoice;
  const isTrial = dynamicInvoiceData.contract?.isTrial ?? false;
  const isInvoiceApproved = reviewStatus === INVOICE_REVIEW_STATUSES.Approved;
  const availableBalance = revenueBreakdown.creditsUsed;
  const queryClient = useQueryClient();

  const navigate = useVyNavigate();

  const isInvoiceApprovableNow = isInvoiceApprovable({ reviewStatus, customer });
  const requiresDraftApprovalWarning = isDraftInvoiceApprovable({ reviewStatus, customer });
  const canApprove = isInvoiceApprovableNow || requiresDraftApprovalWarning;

  const {
    isDialogOpen: isDraftApprovalDialogOpen,
    handleOpen: handleOpenDraftApprovalDialog,
    handleClose: handleCloseDraftApprovalDialog,
  } = useDialog();

  const { data: accountData } = useAccount();

  const shouldDisplayIssueDateAsPending = withDefault(
    accountData?.account.settings.invoicingSettings?.issueOnApproval,
    false,
  );

  const updateInvoicesReviewStatusesMutation = useUpdateInvoicesReviewStatuses();
  const updateInvoiceIssueDateMutation = useUpdateInvoiceIssueDate();

  const shouldIssueByParentCustomer = isInvoiceCustomerChildOfAccumulation({ customer });

  useNotificationTimeout(
    updateInvoicesReviewStatusesMutation.isSuccess,
    updateInvoicesReviewStatusesMutation.reset,
  );

  const handleUpdateInvoiceIssueDate = async (newDate: Date) => {
    await runMutation(
      updateInvoiceIssueDateMutation,
      {
        invoiceId,
        updateInvoiceIssueDateRequest: {
          issuedAt: newDate,
        },
      },
      {
        pending: 'Updating issue date...',
        success: 'Issue date updated',
        error: 'Failed to update issue date',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            predicate: ({ queryKey }) =>
              ['billing-cycle-page', 'billing-cycle-invoices', invoiceId].some((key) =>
                queryKey.includes(key),
              ),
          });
        },
      },
    );
  };

  const handleInvoiceNumberChange = useUpdateInvoiceNumber({
    invoiceId,
    currentInvoiceNumber: invoice.invoiceNumber,
    currentRef: invoice.ref,
  });

  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useExchangeRateDialog();
  const { data: exchangeRatesMatrixData } = useExchangeRatesMatrix();
  const exchangeRatesMatrix = exchangeRatesMatrixData?.data;

  const exchangeRateData = useExchangeRateData();
  const exchangeRateForm = useExchangeRateForm({
    invoiceId,
    contractCurrency,
    initialInvoiceCurrency: invoice.modifiedCurrency ?? invoice.currency,
    initialExchangeRate: invoice.modifiedExchangeRate ?? null,
    exchangeRatesMatrix,
    onCloseDialog: handleCloseDialog,
  });

  const handleApprove = useCallback(async () => {
    await runMutation(
      updateInvoicesReviewStatusesMutation,
      {
        customerIds: [customer.id],
        invoicesByNewStatus: {
          [INVOICE_REVIEW_STATUSES.Approved]: [invoiceId],
        },
      },
      {
        pending: 'Approving invoice...',
        success: 'Invoice approved',
        error: 'Failed to approve invoice',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            predicate: ({ queryKey }) =>
              ['billing-cycle-page', 'billing-cycle-invoices', invoiceId].some((key) =>
                queryKey.includes(key),
              ),
          });

          if (!noRedirectOnApprove) {
            setTimeout(() => {
              navigate(`/invoices/${invoiceId}/customer/${customer.id}/preview`);
            }, 1500);
          }
        },
      },
    );
  }, [
    invoiceId,
    navigate,
    noRedirectOnApprove,
    queryClient,
    updateInvoicesReviewStatusesMutation,
    customer.id,
  ]);

  const handleApproveClick = () => {
    if (requiresDraftApprovalWarning) {
      handleOpenDraftApprovalDialog();
      return;
    }
    void handleApprove();
  };

  return (
    <Paper
      elevation={1}
      sx={{
        display: 'flex',
        maxHeight: '200px',
        flexDirection: 'column',
        gap: 2,
        padding: 3,
        border: 1,
        borderRadius: 2,
        borderColor: 'divider',
      }}
    >
      <InvoiceHeaderInfo customer={customer} invoice={invoice} currency={currency} isTrial={isTrial} />

      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Stack direction="row" sx={{ gap: 2 }} divider={<Divider orientation="vertical" flexItem />}>
          {isNotNullOrUndefined(availableBalance) && availableBalance > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '32px' }}>
              <DynamicInvoiceDetail
                label="Available Balance"
                value={formatPrice(availableBalance, currency, 0, 0)}
              />
            </Box>
          )}
          {isNotEmptyString(contractId) && (
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '32px' }}>
              <NavLink to={`/plans/contracts/${contractId}`}>
                <DynamicInvoiceDetail label="Contract Name" value={invoice.contractName ?? contractId} />
              </NavLink>
            </Box>
          )}
          {isNotNullOrUndefined(invoice.purchaseOrder) && (
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '32px' }}>
              <DynamicInvoiceDetail label="PO Number" value={invoice.purchaseOrder} />
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '32px' }}>
            <EditableTextLabel
              label={invoice.invoiceNumber}
              onSave={handleInvoiceNumberChange}
              variant="subtitle2"
              sx={{ fontSize: '14px', fontWeight: 400 }}
              displayInline
              labelText="Invoice number"
            />
          </Box>
          <DynamicInvoiceIssueDate
            invoice={invoice}
            shouldDisplayIssueDateAsPending={shouldDisplayIssueDateAsPending}
            isInvoiceApproved={isInvoiceApproved}
            onIssueDateSave={handleUpdateInvoiceIssueDate}
          />
          {exchangeRateData.isExchangeRateFeatureActive && (
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '32px' }}>
              <ExchangeRateDisplay
                formattedExchangeRate={exchangeRateData.formattedExchangeRate}
                onEditClick={handleOpenDialog}
                disabled={isInvoiceApproved}
              />
            </Box>
          )}
        </Stack>

        {isInvoiceApproved ? (
          <DynamicInvoiceApprovedLabel approvedAt={invoice.approvedAt} />
        ) : shouldIssueByParentCustomer ? (
          <Typography
            sx={{
              marginLeft: 'auto',
              fontSize: '15px',
              fontWeight: 500,
              color: '#A4ADBE',
            }}
          >
            Issued by {customer.parentCustomer?.name}
          </Typography>
        ) : (
          <Button
            {...getSelectorAttribute(dynamicInvoicePageSelectors.header.approveButton)}
                disabled={!canApprove || updateInvoicesReviewStatusesMutation.isLoading}
                onClick={handleApproveClick}
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<CheckCircleOutlineIcon />}
            sx={{ marginLeft: 'auto' }}
          >
            Approve
          </Button>
        )}
      </Stack>

      {exchangeRateData.isExchangeRateFeatureActive && (
        <ExchangeRateDialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          onSubmit={exchangeRateForm.handleSubmit}
          contractCurrency={exchangeRateData.contractCurrency}
          rateDate={exchangeRateData.rateDate}
          form={exchangeRateForm.form}
          placeholderExchangeRate={exchangeRateForm.placeholderExchangeRate}
          onCurrencyChange={exchangeRateForm.handleCurrencyChange}
          onResetToOriginal={exchangeRateForm.handleResetToOriginal}
        />
      )}

      {requiresDraftApprovalWarning && (
        <ApproveDraftInvoiceDialog
          open={isDraftApprovalDialogOpen}
          onClose={handleCloseDraftApprovalDialog}
          onConfirm={handleApprove}
        />
      )}
    </Paper>
  );
};
